import { getAllPreferences as getAllDefaultPreferences } from '../logic/preferences'
import type { PreferenceDefaults, UserPreferences } from '../models'
import type { PreferenceItem, PreferenceResolutionSource } from '../api/contracts'
import { getAllPreferences as getAllUserPreferences } from '../logic/preferences'
import { isTimeZone } from '@features/shared/utils/timeZones'
import {
  isHomePage,
  isSidebarMenuMode,
  isSidebarNavigationMode,
  isSidebarTableSort,
  type AppDataTypeFormatConfiguration,
  type EffectiveSettings,
  type EmailGreetingBreakpoints,
  type SettingKey,
  type ThemeMode,
} from '../stores/useSettingsStore'

type ResolvedPreference = Pick<
  PreferenceItem,
  'preference_key' | 'resolved_value' | 'resolved_source'
>

export function snapshotEffectiveSettings(state: EffectiveSettings): EffectiveSettings {
  return {
    themeSetting: state.themeSetting,
    homePage: state.homePage,
    sidebarNavigationMode: state.sidebarNavigationMode,
    sidebarMenuMode: state.sidebarMenuMode,
    sidebarTableSort: state.sidebarTableSort,
    sidebarTableFolders: state.sidebarTableFolders,
    advancedGroupSorting: state.advancedGroupSorting,
    exportDownloadAutomatically: state.exportDownloadAutomatically,
    exportOpenWhenReady: state.exportOpenWhenReady,
    sidebarFavoritesSort: state.sidebarFavoritesSort,
    emailGreetingBreakpoints: { ...state.emailGreetingBreakpoints },
    defaultEmailSignOff: state.defaultEmailSignOff,
    appDataTypeFormats: Object.fromEntries(
      Object.entries(state.appDataTypeFormats).map(([key, value]) => [key, { ...value }])
    ),
    holidayCalendar: {
      source: state.holidayCalendar.source,
      customDates: [...state.holidayCalendar.customDates],
    },
    workingHours: { ...state.workingHours },
    organizationTimeZone: state.organizationTimeZone,
    primaryTimeZone: state.primaryTimeZone,
  }
}

function coerceTheme(value: unknown): ThemeMode | null {
  return value === 'light' || value === 'dark' || value === 'system' ? value : null
}

function coerceGreetingBreakpoints(value: unknown): EmailGreetingBreakpoints | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null

  const candidate = value as Partial<Record<keyof EmailGreetingBreakpoints, unknown>>
  const morningStart = Number(candidate.morningStart)
  const afternoonStart = Number(candidate.afternoonStart)
  const eveningStart = Number(candidate.eveningStart)

  if (![morningStart, afternoonStart, eveningStart].every(Number.isFinite)) return null
  return { morningStart, afternoonStart, eveningStart }
}

function coerceAppDataTypeFormat(value: unknown): AppDataTypeFormatConfiguration | null {
  if (typeof value === 'string') return { defaultDisplayFormatId: value }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null

  const candidate = value as Record<string, unknown>
  const defaultDisplayFormatId =
    typeof candidate.defaultDisplayFormatId === 'string' ? candidate.defaultDisplayFormatId : null
  const defaultDisplayFormatName =
    typeof candidate.defaultDisplayFormatName === 'string'
      ? candidate.defaultDisplayFormatName
      : candidate.defaultDisplayFormatName === null
        ? null
        : undefined
  const appDataTypeName =
    typeof candidate.appDataTypeName === 'string'
      ? candidate.appDataTypeName
      : candidate.appDataTypeName === null
        ? null
        : undefined
  const allowedFormats = Array.isArray(candidate.allowedFormats)
    ? candidate.allowedFormats.filter((format): format is string => typeof format === 'string')
    : undefined

  return {
    defaultDisplayFormatId,
    ...(defaultDisplayFormatName !== undefined ? { defaultDisplayFormatName } : {}),
    ...(appDataTypeName !== undefined ? { appDataTypeName } : {}),
    ...(allowedFormats !== undefined ? { allowedFormats } : {}),
  }
}

function coerceAppDataTypeFormats(
  value: unknown
): Record<string, AppDataTypeFormatConfiguration> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null

  const formats: Record<string, AppDataTypeFormatConfiguration> = {}
  for (const [appDataTypeId, rawFormat] of Object.entries(value)) {
    const format = coerceAppDataTypeFormat(rawFormat)
    if (format) formats[appDataTypeId] = format
  }
  return formats
}

function preferenceByKey(preferences: ResolvedPreference[]) {
  return new Map(preferences.map((preference) => [preference.preference_key, preference]))
}

function userPreferenceSource(
  userPreferences: UserPreferences | undefined,
  preferenceKey: string
): PreferenceResolutionSource | null {
  return (
    getAllUserPreferences(userPreferences?.tabs).find(
      (preference) => preference.preference_key === preferenceKey
    )?.resolved_source ?? null
  )
}

function applyResolvedPreferences(
  preferences: ResolvedPreference[],
  canApplyPreference: (preference: ResolvedPreference) => boolean = () => true
): Partial<EffectiveSettings> {
  const patch: Partial<EffectiveSettings> = {}
  const byKey = preferenceByKey(preferences)
  const themePreference = byKey.get('theme')
  const homePagePreference = byKey.get('home_page')
  const greetingPreference = byKey.get('email_greeting_breakpoints')
  const signoffPreference = byKey.get('default_email_signoff')
  const dataTypeFormatsPreference = byKey.get('data_type_formats')

  if (themePreference && canApplyPreference(themePreference)) {
    const theme = coerceTheme(themePreference.resolved_value)
    if (theme) patch.themeSetting = theme
  }

  const applyIfValid = <K extends SettingKey>(
    settingKey: K,
    preferenceKey: string,
    isValid: (value: unknown) => value is EffectiveSettings[K]
  ) => {
    const preference = byKey.get(preferenceKey)
    if (preference && canApplyPreference(preference) && isValid(preference.resolved_value)) {
      patch[settingKey] = preference.resolved_value
    }
  }
  applyIfValid('sidebarNavigationMode', 'sidebar_navigation_mode', isSidebarNavigationMode)
  applyIfValid('sidebarMenuMode', 'sidebar_menu_mode', isSidebarMenuMode)
  applyIfValid('sidebarTableSort', 'sidebar_table_sort', isSidebarTableSort)
  applyIfValid('sidebarFavoritesSort', 'sidebar_favorites_sort', isSidebarTableSort)
  applyIfValid(
    'sidebarTableFolders',
    'sidebar_table_folders',
    (value): value is boolean => typeof value === 'boolean'
  )
  applyIfValid(
    'advancedGroupSorting',
    'advanced_group_sorting',
    (value): value is boolean => typeof value === 'boolean'
  )
  applyIfValid(
    'exportDownloadAutomatically',
    'export_download_automatically',
    (value): value is boolean => typeof value === 'boolean'
  )
  applyIfValid(
    'exportOpenWhenReady',
    'export_open_when_ready',
    (value): value is boolean => typeof value === 'boolean'
  )
  applyIfValid('primaryTimeZone', 'primary_time_zone', isTimeZone)

  if (homePagePreference && canApplyPreference(homePagePreference)) {
    const value = homePagePreference.resolved_value
    patch.homePage = isHomePage(value) ? value : null
  }

  if (greetingPreference && canApplyPreference(greetingPreference)) {
    const greetingBreakpoints = coerceGreetingBreakpoints(greetingPreference.resolved_value)
    if (greetingBreakpoints) patch.emailGreetingBreakpoints = greetingBreakpoints
  }

  if (
    signoffPreference &&
    canApplyPreference(signoffPreference) &&
    typeof signoffPreference.resolved_value === 'string'
  ) {
    patch.defaultEmailSignOff = signoffPreference.resolved_value
  }

  if (dataTypeFormatsPreference && canApplyPreference(dataTypeFormatsPreference)) {
    const appDataTypeFormats = coerceAppDataTypeFormats(dataTypeFormatsPreference.resolved_value)
    if (appDataTypeFormats) patch.appDataTypeFormats = appDataTypeFormats
  }

  return patch
}

export function effectiveSettingsPatchFromPreferenceDefaults(
  defaults: PreferenceDefaults | undefined,
  userPreferences: UserPreferences | undefined
): Partial<EffectiveSettings> {
  if (!defaults) return {}

  return applyResolvedPreferences(getAllDefaultPreferences(defaults.tabs), (preference) => {
    const source = userPreferenceSource(userPreferences, preference.preference_key)
    return source === null || source === 'default'
  })
}

export function effectiveSettingsPatchFromUserPreferences(
  preferences: UserPreferences | undefined
): Partial<EffectiveSettings> {
  return preferences ? applyResolvedPreferences(getAllUserPreferences(preferences.tabs)) : {}
}
